import React, { Fragment, useState, useEffect } from 'react';
import { Field } from 'redux-form';
import PropTypes from 'prop-types';
import renderField from './RenderField';
import { cepMasker, cellPhoneMasker } from './Masks';
import Select from './Select';
import GraphQL from '../../../GraphQL';
import stateListAllQuery from '../../queries/State';
import cityListByStateQuery from '../../queries/City';
import { showNotificationAutoDestroy } from '../ShowNotification';
import validateCep from '../../queries/CepValidation';

export default function AddressInfo(props) {
  const { t, showLoading, hideLoading, change, disabled, hideFields } = props;

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [zipCodeData, setZipCodeData] = useState();
  const [classRowPhones, setClassRowPhones] = useState(
    'form__form-group--3_columns'
  );
  const [classRowAddresAdj, setClassRowAddresAdj] = useState(
    'form__form-group--3_columns'
  );

  const loadStates = () => {
    const query = stateListAllQuery();
    showLoading();
    GraphQL(query, {})
      .then((res) => {
        const loadedStates = res.stateListAll.map((item) => ({
          ...item,
          value: item.uf,
          label: item.name,
        }));
        setStates(loadedStates);
        hideLoading();
      })
      .catch((error) => {
        console.error(error);
        showNotificationAutoDestroy(
          'danger',
          t('notifications.error'),
          t('address.errors.load-state-error')
        );
        hideLoading();
      });
  };

  const loadCities = async (ufId) => {
    const query = cityListByStateQuery(ufId);
    showLoading();
    await GraphQL(query, {})
      .then((res) => {
        hideLoading();
        if (res.cityListByState) {
          const loadedCities = res.cityListByState.map((item) => ({
            ...item,
            value: item.id,
            label: item.name,
          }));
          setCities(loadedCities);
        }
      })
      .catch((error) => {
        hideLoading();
        console.error(error);
        showNotificationAutoDestroy(
          'danger',
          t('notifications.error'),
          t('address.errors.load-cities-error')
        );
      });
  };

  useEffect(() => {
    loadStates();
    if (hideFields.cellPhone && hideFields.cellPhoneAux) {
      setClassRowPhones('form__form-group');
    } else if (hideFields.cellPhone || hideFields.cellPhoneAux) {
      setClassRowPhones('form__form-group--2_columns');
    }
    if (hideFields.addressAdjunct && hideFields.neighborhood) {
      setClassRowAddresAdj('form__form-group');
    } else if (hideFields.addressAdjunct || hideFields.neighborhood) {
      setClassRowAddresAdj('form__form-group--2_columns');
    }
  }, []);

  useEffect(() => {
    if (zipCodeData) {
      const selectedCity = cities.find(
        (item) => item.ibgeId.toString() === zipCodeData.ibge
      );
      change('city', selectedCity);
    }
  }, [cities]);

  const zipCodeOnBlurHandler = async (evt) => {
    const zipCode = evt.target.value.replace('_', '');
    if (zipCode && zipCode.length === 9) {
      showLoading();
      validateCep(zipCode)
        .then((res) => {
          if (res.data && res.data.cep) {
            setZipCodeData(res.data);
            change('address', res.data.logradouro ? res.data.logradouro : '');
            change('neighborhood', res.data.bairro ? res.data.bairro : '');
            const selectedState = states.find(
              (item) => item.uf === res.data.uf
            );
            change('state', {
              ...selectedState,
              value: selectedState.uf,
            });
            loadCities(selectedState.id);
          } else {
            showNotificationAutoDestroy(
              'danger',
              t('notifications.error'),
              t('address.errors.invalid-zipcode')
            );
            change('address', '');
            change('neighborhood', '');
            change('state', null);
            change('city', null);
            change('city', null);
            change('zipCode', null);
            setCities([]);
            setZipCodeData(null);
          }
          hideLoading();
        })
        .catch((erro) => {
          hideLoading();
          console.error(erro);
        });
    } else {
      change('address', '');
      change('neighborhood', '');
      change('state', null);
      change('city', null);
      setCities([]);
      setZipCodeData(null);
    }
  };

  const onUfChangeHandler = (value) => {
    if (value && value.id) {
      change('state', value);
      loadCities(value.id);
    }
    change('city', null);
  };

  return (
    <Fragment>
      <div className="form__form-group--2_columns">
        <div className="form__form-group col-md-1">
          <span className="form__form-group-label">
            {t('address.zip-code')}
          </span>
          <div className="form__form-group-field">
            <Field
              name="zipCode"
              type="text"
              component={renderField}
              {...cepMasker()}
              placeholder={t('placeholder.student.zipCode')}
              onBlur={zipCodeOnBlurHandler}
              disabled={disabled}
            />
          </div>
        </div>
        <div className="form__form-group">
          <span className="form__form-group-label">{t('address.address')}</span>
          <div className="form__form-group-field">
            <Field
              name="address"
              type="text"
              component={renderField}
              maxLength="200"
              placeholder={t('placeholder.student.address')}
              disabled={disabled}
            />
          </div>
        </div>
      </div>
      <div className={classRowAddresAdj}>
        <div className="form__form-group">
          <span className="form__form-group-label">{t('address.number')}</span>
          <div className="form__form-group-field">
            <Field
              name="addressNumber"
              type="text"
              component={renderField}
              maxLength="25"
              placeholder={t('placeholder.student.addressNumber')}
              disabled={disabled}
            />
          </div>
        </div>
        {!hideFields.neighborhood && (
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('address.neighborhood')}
            </span>
            <div className="form__form-group-field">
              <Field
                name="neighborhood"
                type="text"
                component={renderField}
                maxLength="100"
                placeholder={t('placeholder.student.neighborhood')}
                disabled={disabled}
              />
            </div>
          </div>
        )}
        {!hideFields.addressAdjunct && (
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('address.adjunct')}
            </span>
            <div className="form__form-group-field">
              <Field
                name="addressAdjunct"
                type="text"
                component={renderField}
                maxLength="100"
                placeholder={t('placeholder.student.addressAdjunct')}
                disabled={disabled}
              />
            </div>
          </div>
        )}
      </div>
      <div className="form__form-group--2_columns">
        {!hideFields.state && (
          <div className="form__form-group">
            <span className="form__form-group-label">{t('address.state')}</span>
            <div className="form__form-group-field">
              <Field
                name="state"
                type="text"
                options={states}
                component={Select}
                placeholder={t('placeholder.student.state')}
                onChange={onUfChangeHandler}
                isDisabled={disabled}
              />
            </div>
          </div>
        )}
        {!hideFields.city && (
          <div className="form__form-group">
            <span className="form__form-group-label">{t('address.city')}</span>
            <div className="form__form-group-field">
              <Field
                name="city"
                type="text"
                options={cities}
                component={Select}
                placeholder={t('placeholder.student.city')}
                isDisabled={disabled}
              />
            </div>
          </div>
        )}
      </div>
      <div className={classRowPhones}>
        {!hideFields.cellPhone && (
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('phone.cellphone')}
            </span>
            <div className="form__form-group-field">
              <Field
                name="cellPhone"
                type="text"
                component={renderField}
                {...cellPhoneMasker()}
                placeholder={t('placeholder.student.cellPhone')}
                disabled={disabled}
              />
            </div>
          </div>
        )}
        {!hideFields.cellPhoneAux && (
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('phone.whatsapp')}
            </span>
            <div className="form__form-group-field">
              <Field
                name="cellPhoneAux"
                type="text"
                component={renderField}
                {...cellPhoneMasker()}
                placeholder={t('placeholder.student.cellPhoneAux')}
                disabled={disabled}
              />
            </div>
          </div>
        )}
        <div className="form__form-group">
          <span className="form__form-group-label">{t('phone.phone')}</span>
          <div className="form__form-group-field">
            <Field
              name="phone"
              type="text"
              component={renderField}
              {...cellPhoneMasker()}
              placeholder={t('placeholder.student.phone')}
              disabled={disabled}
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
}

AddressInfo.propTypes = {
  t: PropTypes.func.isRequired,
  showLoading: PropTypes.func.isRequired,
  hideLoading: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  states: PropTypes.arrayOf(PropTypes.shape),
  cities: PropTypes.arrayOf(PropTypes.shape),
  disabled: PropTypes.bool,
  hideFields: PropTypes.shape({
    cellPhone: PropTypes.bool,
    cellPhoneAux: PropTypes.bool,
    neighborhood: PropTypes.bool,
    addressAdjunct: PropTypes.bool,
    state: PropTypes.bool,
    city: PropTypes.bool,
  }),
};

AddressInfo.defaultProps = {
  states: [],
  cities: [],
  disabled: false,
  hideFields: {
    cellPhone: false,
    cellPhoneAux: false,
    neighborhood: false,
    addressAdjunct: false,
  },
};
